import {
  makeInitialState,
  teardownResults,
  revokeUrl,
  type AppState,
  type InputFile,
  type LogoOptions,
  type HashtagOptions,
  type ResultItem,
} from "../state.js";

export type AppAction =
  | { type: "SET_INPUTS"; inputs: InputFile[] }
  | { type: "SET_ERROR"; message: string | null }
  | { type: "SET_LOGO"; logo: LogoOptions }
  | { type: "SET_HASHTAG"; hashtag: HashtagOptions }
  | { type: "START_PROCESSING"; abortController: AbortController }
  | { type: "UPDATE_PROGRESS"; current: number; total: number }
  | { type: "ADD_RESULT"; result: ResultItem }
  | { type: "APPEND_ERROR"; inputName: string; message: string }
  | { type: "FINISH_PROCESSING" }
  | { type: "TOGGLE_SELECT"; id: string }
  | { type: "SELECT_ALL"; selected: boolean }
  | { type: "OPEN_LIGHTBOX"; id: string }
  | { type: "CLOSE_LIGHTBOX" };

export function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case "SET_INPUTS": {
      const removedIds = new Set(
        state.inputs
          .filter((i) => !action.inputs.some((n) => n.id === i.id))
          .map((i) => i.id),
      );
      for (const inp of state.inputs) {
        if (removedIds.has(inp.id)) revokeUrl(inp.thumbUrl);
      }
      return { ...state, inputs: action.inputs };
    }
    case "SET_ERROR":
      return { ...state, errorMessage: action.message };
    case "SET_LOGO":
      return { ...state, logo: action.logo };
    case "SET_HASHTAG":
      return { ...state, hashtag: action.hashtag };
    case "START_PROCESSING":
      teardownResults(state.results);
      return {
        ...state,
        phase: "processing",
        results: [],
        errorMessage: null,
        progress: { current: 0, total: 0, abortController: action.abortController },
      };
    case "UPDATE_PROGRESS":
      if (!state.progress) return state;
      return {
        ...state,
        progress: { ...state.progress, current: action.current, total: action.total },
      };
    case "ADD_RESULT":
      return { ...state, results: [...state.results, action.result] };
    case "APPEND_ERROR": {
      const prev = state.errorMessage ? `${state.errorMessage}\n` : "";
      return { ...state, errorMessage: `${prev}[${action.inputName}] ${action.message}` };
    }
    case "FINISH_PROCESSING":
      return { ...state, phase: "done", progress: null };
    case "TOGGLE_SELECT":
      return {
        ...state,
        results: state.results.map((r) =>
          r.id === action.id ? { ...r, selected: !r.selected } : r,
        ),
      };
    case "SELECT_ALL":
      return {
        ...state,
        results: state.results.map((r) => ({ ...r, selected: action.selected })),
      };
    case "OPEN_LIGHTBOX":
      return { ...state, lightboxResultId: action.id };
    case "CLOSE_LIGHTBOX":
      return { ...state, lightboxResultId: null };
  }
}

export { makeInitialState };
